import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
    Button,
    Grid,
    Typography,
    Paper,
    CircularProgress,
} from '@material-ui/core';
import {FormProvider, useForm} from 'react-hook-form';
import {Text} from 'components/forms/fields';
import {joiResolver} from '@hookform/resolvers/joi';
import joiMessages from 'components/forms/locale/validation';
import Joi from 'joi';
import usePostForm from 'hooks/usePostForm';
import {Alert} from '@material-ui/lab';
import Fab from '@material-ui/core/Fab';
import {Modal, Fade, Backdrop} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import {LifeRing} from 'components/icons/icons.jsx';

const useStyles = makeStyles(theme => ({
    fab: {
        position: 'fixed',
        bottom: theme.spacing(0),
        left: 'auto',
        margin: '1rem',
    },

    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: 'none',
    },
    backdrop: {
        backdropFilter: 'blur(5px)',
    },
    root: {
        width: '50%',
        margin: 'auto',
        padding: '1rem',
        maxWidth: 500,
        outline: 0,

        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
        [theme.breakpoints.only('sm')]: {
            width: '90%',
        },
    },
    form: {
        width: '100%',
        padding: '1rem',
    },
    paper: {
        padding: '1rem',
        margin: 'auto',
        maxWidth: 500,
    },
}));

const schema = {
    feedback_text: Joi.string().required(
        'Please describe your issue in the text box.'
    ),
};

const Support = () => {
    const classes = useStyles();
    const [open, setOpen] = React.useState(false);
    const [alerts, setAlerts] = useState([]);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setAlerts();
    };

    const methods = useForm({
        defaultValues: {
            feedback_text: '',
        },
        mode: 'onTouched',
        resolver: joiResolver(
            Joi.object(schema).messages(joiMessages).unknown(true)
        ),
    });

    //Support posting
    const {submissionInProgress, error, result, post} = usePostForm('feedback');

    const onSubmit = data => {
        post(data);
        methods.reset();
    };

    useEffect(() => {
        if (result?.status === 'ok') {
            const newAlert = {
                severity: 'ok',
                message:
                    'Thanks for contacting us. We will get back to you as soon as we can!',
            };
            setAlerts([...alerts, newAlert]);
        } else if (result?.errors?.length || error?.errors?.length) {
            const newAlert = {
                severity: 'error',
                message: `Something went wrong. Errors: ${JSON.stringify(
                    error
                )}`,
            };
            setAlerts([...alerts, newAlert]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result, error]);

    return (
        <div>
            <Fab
                variant='extended'
                className={classes.fab}
                size='small'
                color='secondary'
                aria-label='support'
                onClick={handleOpen}
            >
                <LifeRing width='24' />
                &nbsp; help
            </Fab>

            <Modal
                className={classes.modal}
                open={open}
                onClose={handleClose}
                closeAfterTransition
                outline='none'
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                    className: classes.backdrop,
                }}
            >
                <Fade in={open}>
                    <Grid className={classes.root}>
                        <Paper className={classes.paper}>
                            <Grid
                                container
                                direction='row'
                                justifyContent='flex-end'
                                alignItems='flex-start'
                            >
                                <IconButton
                                    color='primary'
                                    aria-label='close'
                                    edge='end'
                                    onClick={handleClose}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Grid>
                            <Grid>
                                <FormProvider {...methods}>
                                    <form
                                        onSubmit={methods.handleSubmit(
                                            onSubmit
                                        )}
                                        className={classes.form}
                                    >
                                        <Typography variant='h6'>
                                            I have encountered an issue and
                                            require assistance.
                                        </Typography>
                                        <p></p>
                                        <Text
                                            name='feedback_text'
                                            label='What went wrong? Let us know'
                                            multiline
                                            rows={8}
                                            fullWidth
                                            placeholder='Describe the issue here.'
                                            variant='outlined'
                                        />
                                        <p></p>
                                        <Grid
                                            container
                                            direction='row'
                                            justifyContent='flex-end'
                                        >
                                            <Button
                                                type='submit'
                                                variant='contained'
                                                color='secondary'
                                            >
                                                Send
                                            </Button>
                                        </Grid>
                                        <div></div>
                                    </form>
                                </FormProvider>
                                {submissionInProgress && <CircularProgress />}
                                {alerts?.map(alert => {
                                    return (
                                        <Alert severity={alert.severity}>
                                            {alert.message}
                                        </Alert>
                                    );
                                })}
                            </Grid>
                        </Paper>
                    </Grid>
                </Fade>
            </Modal>
        </div>
    );
};

export default Support;
